/**
 * PromptEditor Component
 *
 * Cursor-style textarea with inline generate button
 * Following SOLID principles:
 * - Single Responsibility: Handles prompt input and generation trigger
 * - Open/Closed: Extensible through props without modification
 * - Interface Segregation: Only exposes necessary props
 */

import React from "react";
import { Input, Button, Badge } from "antd";
import {
  SendOutlined,
  ClearOutlined,
  HistoryOutlined,
} from "@ant-design/icons";
import { PromptEditorProps } from "../../types/Step0.types";
import "./index.scss";

const { TextArea } = Input;

/**
 * PromptEditor Component
 *
 * Features:
 * - Cursor-style placeholder text
 * - Auto-resize textarea (6-12 rows)
 * - Inline generate button with highlight states
 * - Disabled state during generation
 * - Keyboard shortcuts (Ctrl+Enter to generate)
 */
const PromptEditor: React.FC<PromptEditorProps> = ({
  prompt,
  onPromptChange,
  onGenerate,
  isGenerating,
  maxRows = 6,
  disabled = false,
  placeholder = "Kickstart your hiring process: describe the role, and let AI do the rest.",
  historyCount,
  onHistoryClick,
}) => {
  /**
   * Handle keyboard shortcuts
   * Ctrl+Enter or Cmd+Enter triggers generation
   */
  const handleKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if ((e.ctrlKey || e.metaKey) && e.key === "Enter") {
      e.preventDefault();
      if (prompt.trim() && !isGenerating) {
        onGenerate();
      }
    }
  };

  /**
   * Handle textarea change
   * Debounced to prevent excessive re-renders
   */
  const handleTextChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    onPromptChange(e.target.value);
  };

  /**
   * Handle generate button click
   * Validates prompt before generation
   */
  const handleGenerateClick = () => {
    if (prompt.trim() && !isGenerating) {
      onGenerate();
    }
  };

  /**
   * Handle clear button click
   * Clears the prompt text
   */
  const handleClearClick = () => {
    onPromptChange("");
  };

  /**
   * Check if generate button should be active
   * Button is active when prompt has content, not generating, and not disabled
   */
  const isGenerateActive =
    prompt.trim().length > 0 && !isGenerating && !disabled;

  return (
    <div className="prompt-editor-container">
      <div className="prompt-editor-wrapper">
        <div className="input-container">
          <div className="ai-icon-wrapper">
            <img
              src="/icons/ai-stars.svg"
              alt="AI"
              className="ai-icon ai-icon-shine"
            />
          </div>
          <TextArea
            value={prompt}
            onChange={handleTextChange}
            onKeyDown={handleKeyDown}
            placeholder={placeholder}
            autoSize={{ minRows: 1, maxRows }}
            className="cursor-style-textarea"
            disabled={isGenerating}
            autoFocus
            variant="borderless"
          />

          <div className="input-actions">
            {historyCount !== undefined &&
              historyCount > 0 &&
              onHistoryClick && (
                <Badge count={historyCount} showZero={false} offset={[-2, 2]}>
                  <Button
                    type="text"
                    icon={<HistoryOutlined />}
                    onClick={onHistoryClick}
                    disabled={isGenerating}
                    className="history-button"
                    size="large"
                    shape="circle"
                    title="View refinement history"
                  />
                </Badge>
              )}
            {prompt.trim() && (
              <Button
                type="text"
                icon={<ClearOutlined />}
                onClick={handleClearClick}
                disabled={isGenerating}
                className="clear-button"
                size="large"
                shape="circle"
                title="Clear text"
              />
            )}
            <Button
              type="primary"
              icon={<SendOutlined />}
              onClick={handleGenerateClick}
              disabled={!isGenerateActive}
              loading={isGenerating}
              className={`generate-button ${
                isGenerateActive
                  ? "generate-button--active"
                  : "generate-button--inactive"
              }`}
              size="large"
              shape="circle"
            />
          </div>
        </div>
      </div>
    </div>
  );
};

export default PromptEditor;
